import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import fnc from '../../mymod/commFunction';
import QuebankQuestionCount from './QuebankQuestionCount';
import WalletTransactionSummary from './WalletTransactionSummary';
import UsageContents from './usageContents/UsageContents';
import ConversionErrorHistory from './ConversionErrorHistory';
import './LoginHistory.scss';

const TAB = {
    LOGIN: 'login',
    MENU: 'menu',
    USAGE: 'usage',
    ERRORS: 'errors',
    WALLET: 'wallet',
    QUEBANK: 'quebank'
};

const BILL_FILTER = {
    ALL: 'all',
    MATHTAILER: 'mathtailer',
    KUESB_LMS: 'kuesbLms'
};

const EXCLUDED_USER_ID = 'mathautogen';
const MATHAUTOGEN_USER_NAME = '홍대화';
const MEMBER_FILTER_STORAGE_KEY = 'adminMemberListFilters';
const HISTORY_TAB_STORAGE_KEY = 'adminLoginHistoryTab';
const USAGE_SELECTION_STORAGE_KEY = 'adminUsageHistorySelectedRows';
const USAGE_CONTENTS_VIEW_STORAGE_KEY = 'adminUsageContentsViewOpen';
const PDF2_IMAGE_PROCESS_SERVICE_NAMES = new Set([
    'PDF2_IMAGE_ALL_IMAGES_SIMPLE',
    'PDF2_IMAGE_PRECISE_FAST_PROMPT'
]);

function getStoredHistoryTab() {
    if (typeof window === 'undefined') return TAB.LOGIN;

    try {
        const storedTab = window.sessionStorage.getItem(HISTORY_TAB_STORAGE_KEY);
        return Object.values(TAB).includes(storedTab) ? storedTab : TAB.LOGIN;
    } catch (error) {
        return TAB.LOGIN;
    }
}

function getStoredUsageContentsView() {
    if (typeof window === 'undefined') return false;

    try {
        return window.sessionStorage.getItem(USAGE_CONTENTS_VIEW_STORAGE_KEY) === 'true';
    } catch (error) {
        return false;
    }
}

function getStoredUsageSelection() {
    if (typeof window === 'undefined') return new Set();

    try {
        const raw = window.sessionStorage.getItem(USAGE_SELECTION_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        return new Set(Array.isArray(parsed) ? parsed.map(String) : []);
    } catch (error) {
        return new Set();
    }
}

function storeUsageSelection(selectedKeys) {
    if (typeof window === 'undefined') return;

    try {
        window.sessionStorage.setItem(
            USAGE_SELECTION_STORAGE_KEY,
            JSON.stringify(Array.from(selectedKeys))
        );
    } catch (error) {
        // 저장소를 사용할 수 없는 환경에서는 현재 화면의 선택 상태만 유지한다.
    }
}

function normalizeFilterValue(value) {
    if (value === null || value === undefined || value === 'NULL') return '';
    return String(value).trim().toLowerCase();
}

function formatCell(value) {
    if (value === null || value === undefined || value === 'NULL') return '';
    return String(value);
}

function formatWalletRefType(refType, refId) {
    const originalRefType = formatCell(refType);
    if (originalRefType !== 'PDF_PRO') return originalRefType;

    const matchedRefType = formatCell(refId).match(/(?:^|:)PDF_PRO_(\d+)(?=:|$)/i);
    return matchedRefType ? `PDF_PRO${Number(matchedRefType[1])}` : originalRefType;
}

function getStoredMemberFilters() {
    if (typeof window === 'undefined') {
        return {
            billFilter: BILL_FILTER.MATHTAILER,
            keyword: ''
        };
    }

    try {
        const raw = window.sessionStorage.getItem(MEMBER_FILTER_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : {};
        const normalizedBillFilter = normalizeFilterValue(parsed?.billFilter);

        return {
            billFilter: normalizedBillFilter === normalizeFilterValue(BILL_FILTER.ALL)
                ? BILL_FILTER.ALL
                : normalizedBillFilter === normalizeFilterValue(BILL_FILTER.KUESB_LMS)
                    ? BILL_FILTER.KUESB_LMS
                    : BILL_FILTER.MATHTAILER,
            keyword: String(parsed?.keyword || '')
        };
    } catch (error) {
        return {
            billFilter: BILL_FILTER.MATHTAILER,
            keyword: ''
        };
    }
}

function formatCost4(value) {
    if (value === null || value === undefined || value === '' || value === 'NULL') return '';
    const num = Number(value);
    if (Number.isNaN(num)) return formatCell(value);
    return num.toFixed(4);
}

function formatAmount(value) {
    const num = Number(value);
    if (!Number.isFinite(num)) return '0';

    return num.toLocaleString('ko-KR', {
        maximumFractionDigits: 4
    });
}

function formatNumberCell(value) {
    if (value === null || value === undefined || value === '' || value === 'NULL') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return formatCell(value);

    return num.toLocaleString('ko-KR', {
        maximumFractionDigits: 4
    });
}

function formatNumberCell1(value) {
    if (value === null || value === undefined || value === '' || value === 'NULL') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return formatCell(value);

    return num.toLocaleString('ko-KR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
    });
}

function getFinancialSignClass(value) {
    const number = Number(value);
    if (!Number.isFinite(number) || number === 0) return '';
    return number < 0 ? 'td-finance-negative' : 'td-finance-positive';
}

function formatRoundedNumberCell(value) {
    if (value === null || value === undefined || value === '' || value === 'NULL') return '';
    const num = Number(value);
    if (!Number.isFinite(num)) return formatCell(value);

    return Math.round(num).toLocaleString('ko-KR');
}

function formatRemainingRatio(balanceAfter, lastChargeAmount) {
    const balance = Number(balanceAfter);
    const lastCharge = Number(lastChargeAmount);

    if (!Number.isFinite(balance) || !Number.isFinite(lastCharge) || lastCharge === 0) return '';
    return `${Math.round((balance / lastCharge) * 100)}%`;
}

function formatDurationSeconds(value) {
    const durationSeconds = Math.max(0, Math.floor(Number(value)));
    if (!Number.isFinite(durationSeconds) || durationSeconds === 0) return '';
    if (durationSeconds < 60) return String(durationSeconds);

    const minutes = Math.floor(durationSeconds / 60);
    const seconds = durationSeconds % 60;
    return `${minutes}분 ${seconds}`;
}

function isTodayDate(value) {
    const text = formatCell(value);
    if (!text) return false;

    const today = new Date();
    const todayText = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    const matched = text.match(/^(\d{4}-\d{2}-\d{2})/);
    if (matched) {
        return matched[1] === todayText;
    }

    const parsed = new Date(text);
    if (Number.isNaN(parsed.getTime())) return false;

    return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}` === todayText;
}

const LoginHistory = () => {
    const navigate = useNavigate();
    const storedMemberFilters = getStoredMemberFilters();

    const [tab, setTab] = useState(getStoredHistoryTab);
    const [errorJobId, setErrorJobId] = useState('');
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
    const [searchFilter, setSearchFilter] = useState(storedMemberFilters.keyword);
    const [excludeMathautogen, setExcludeMathautogen] = useState(true);
    const [showMathautogenOnly, setShowMathautogenOnly] = useState(false);
    const [billFilter, setBillFilter] = useState(storedMemberFilters.billFilter);
    const [selectedRowKeys, setSelectedRowKeys] = useState(() => new Set());
    const [completedUsageTotalCostKrw, setCompletedUsageTotalCostKrw] = useState(null);
    const [copyCellValue, setCopyCellValue] = useState(null);
    const [usageCopyStatus, setUsageCopyStatus] = useState('idle');
    const [summaryRefreshToken, setSummaryRefreshToken] = useState(0);
    const [todayAmountTotal, setTodayAmountTotal] = useState(null);
    const [showUsageContents, setShowUsageContents] = useState(getStoredUsageContentsView);
    const [showUsageWarningColors, setShowUsageWarningColors] = useState(false);
    const rowDragSelectionRef = useRef({ active: false, mode: 'add' });
    const usageCopyInputRef = useRef(null);
    const usageCopyMobileInputRef = useRef(null);
    const usageCopyResetTimerRef = useRef(null);
    const usageWarningRevealTimerRef = useRef(null);
    const historyRequestIdRef = useRef(0);

    useEffect(() => {
        if (tab === TAB.USAGE) {
            setSortConfig({ key: 'job_id', direction: 'desc' });
        } else if (tab === TAB.WALLET) {
            setSortConfig({ key: 'created_at', direction: 'desc' });
        } else {
            setSortConfig({ key: '', direction: 'asc' });
        }
        setSelectedRowKeys(new Set());
        setCompletedUsageTotalCostKrw(null);
        setCopyCellValue(null);
        setUsageCopyStatus('idle');
        if (tab === TAB.QUEBANK || tab === TAB.ERRORS) {
            setLoading(false);
        } else {
            fetchHistory(tab);
        }
    }, [tab]);

    useEffect(() => {
        const stopRowDragSelection = () => {
            if (rowDragSelectionRef.current.active) {
                setCompletedUsageTotalCostKrw(
                    rowDragSelectionRef.current.pendingCount > 0
                        ? rowDragSelectionRef.current.pendingUsageTotalCostKrw
                        : null
                );
            }
            rowDragSelectionRef.current.active = false;
        };

        window.addEventListener('mouseup', stopRowDragSelection);
        return () => {
            window.removeEventListener('mouseup', stopRowDragSelection);
            if (usageCopyResetTimerRef.current) {
                window.clearTimeout(usageCopyResetTimerRef.current);
            }
            if (usageWarningRevealTimerRef.current) {
                window.clearTimeout(usageWarningRevealTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        window.sessionStorage.setItem(MEMBER_FILTER_STORAGE_KEY, JSON.stringify({
            billFilter,
            keyword: searchFilter
        }));
    }, [billFilter, searchFilter]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        try {
            window.sessionStorage.setItem(HISTORY_TAB_STORAGE_KEY, tab);
        } catch (error) {
            // 저장소를 사용할 수 없는 환경에서는 기본 탭으로 동작한다.
        }
    }, [tab]);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        try {
            window.sessionStorage.setItem(
                USAGE_CONTENTS_VIEW_STORAGE_KEY,
                showUsageContents ? 'true' : 'false'
            );
        } catch (error) {
            // 저장소를 사용할 수 없는 환경에서는 현재 화면 상태만 유지한다.
        }
    }, [showUsageContents]);

    const activeSearchFilter = searchFilter || '';

    const filteredRows = useMemo(() => {
        const searchKeyword = normalizeFilterValue(activeSearchFilter);
        const billKeyword = normalizeFilterValue(billFilter);

        return rows.filter((item) => {
            const isMatchedIntegratedKeyword = !searchKeyword
                || normalizeFilterValue(item?.user_id).includes(searchKeyword)
                || normalizeFilterValue(item?.user_name).includes(searchKeyword);
            const isMatchedBillId = billKeyword === BILL_FILTER.ALL || normalizeFilterValue(item?.bill_id) === billKeyword;
            const isExcludedMathautogen = !excludeMathautogen
                || normalizeFilterValue(item?.user_id) !== EXCLUDED_USER_ID;
            const isMatchedMathautogenName = !showMathautogenOnly
                || normalizeFilterValue(item?.user_name) === normalizeFilterValue(MATHAUTOGEN_USER_NAME);

            return isMatchedIntegratedKeyword
                && isMatchedBillId
                && isExcludedMathautogen
                && isMatchedMathautogenName;
        });
    }, [rows, activeSearchFilter, billFilter, excludeMathautogen, showMathautogenOnly]);

    const hasActiveFilters = useMemo(() => {
        return Boolean(
            normalizeFilterValue(activeSearchFilter)
            || normalizeFilterValue(billFilter) !== BILL_FILTER.ALL
            || showMathautogenOnly
        );
    }, [activeSearchFilter, billFilter, showMathautogenOnly]);

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return filteredRows;

        const numericFields = new Set([
            'sno',
            'id',
            'amount',
            'wallet_amount',
            'original_cost',
            'profit',
            'margin_rate',
            'balance_after',
            'remaining_ratio',
            'last_charge_amount',
            'total_charged',
            'pageCnt',
            'total_page',
            'page_number',
            'input_cost_usd',
            'output_cost_usd',
            'total_cost_usd',
            'total_cost_krw'
        ]);

        const toSortValue = (item, key) => {
            if (key === 'remaining_ratio') {
                const balance = Number(item?.balance_after);
                const lastChargeAmount = Number(item?.last_charge_amount);
                return Number.isFinite(balance) && Number.isFinite(lastChargeAmount) && lastChargeAmount !== 0
                    ? (balance / lastChargeAmount) * 100
                    : null;
            }

            if (key === 'ref_type') {
                return formatWalletRefType(item?.ref_type, item?.ref_id).toLowerCase();
            }

            const raw = item?.[key];
            if (raw === null || raw === undefined || raw === '') return null;

            if (numericFields.has(key)) {
                const n = Number(raw);
                return Number.isNaN(n) ? null : n;
            }

            if (key === 'created_at') {
                const ts = Date.parse(String(raw));
                return Number.isNaN(ts) ? String(raw).toLowerCase() : ts;
            }

            return String(raw).toLowerCase();
        };

        return [...filteredRows].sort((a, b) => {
            const av = toSortValue(a, sortConfig.key);
            const bv = toSortValue(b, sortConfig.key);

            if (av === null && bv === null) return 0;
            if (av === null) return 1;
            if (bv === null) return -1;

            let result = 0;
            if (typeof av === 'number' && typeof bv === 'number') {
                result = av - bv;
            } else {
                result = String(av).localeCompare(String(bv), 'ko');
            }

            return sortConfig.direction === 'asc' ? result : -result;
        });
    }, [filteredRows, sortConfig]);

    const usageGroupColorIndexes = useMemo(() => {
        let groupIndex = -1;
        let previousGroupKey = '';

        return sortedRows.map((item, index) => {
            const jobId = formatCell(item?.job_id).trim();
            const groupKey = jobId || `row-${index}`;

            if (index === 0 || groupKey !== previousGroupKey) {
                groupIndex = (groupIndex + 1) % 6;
                previousGroupKey = groupKey;
            }

            return groupIndex;
        });
    }, [sortedRows]);

    function handleRefresh() {
        fetchHistory(tab);
        setSummaryRefreshToken((prev) => prev + 1);
    }

    function handleTabChange(nextTab) {
        if (nextTab === tab) {
            setShowUsageContents(false);
            return;
        }

        // 이전 탭의 최대 2,000행을 새 탭 형식으로 다시 그리는 렌더를 막는다.
        historyRequestIdRef.current += 1;
        setShowUsageContents(false);
        setRows([]);
        setLoading(nextTab !== TAB.QUEBANK && nextTab !== TAB.ERRORS);
        setTab(nextTab);
    }

    function handleFilterChange(value) {
        setSearchFilter(value);
    }

    function handleResetFilters() {
        setSearchFilter('');
        setBillFilter(BILL_FILTER.MATHTAILER);
        setExcludeMathautogen(true);
        setShowMathautogenOnly(false);
    }

    function fetchHistory(requestedTab = tab) {
        const requestId = historyRequestIdRef.current + 1;
        historyRequestIdRef.current = requestId;

        const applyHistoryRows = (res, onApplied) => {
            // 빠르게 탭을 바꿨을 때 이전 요청의 늦은 응답이 현재 탭을 덮지 않게 한다.
            if (historyRequestIdRef.current !== requestId) return;

            const nextRows = Array.isArray(res) ? res : [];
            setRows(nextRows);
            onApplied?.(nextRows);
            setLoading(false);
        };

        setLoading(true);

        if (requestedTab === TAB.USAGE) {
            setShowUsageWarningColors(false);
            if (usageWarningRevealTimerRef.current) {
                window.clearTimeout(usageWarningRevealTimerRef.current);
                usageWarningRevealTimerRef.current = null;
            }
        }

        if (requestedTab === TAB.LOGIN) {
            fnc.sendAxios('/userRoutes/selectLoginHistory', {
                rowLimit: 2000
            }, (res) => {
                applyHistoryRows(res);
            });
            return;
        }

        if (requestedTab === TAB.MENU) {
            fnc.sendAxios('/userRoutes/selectMenuClickHistory', {
                rowLimit: 2000
            }, (res) => {
                applyHistoryRows(res);
            });
            return;
        }

        if (requestedTab === TAB.USAGE) {
            fnc.sendAxios('/userRoutes/selectAiTokenUsageHistory', {
                rowLimit: 2000
            }, (res) => {
                applyHistoryRows(res, (nextRows) => {
                    const storedSelection = getStoredUsageSelection();
                    const availableRowKeys = new Set(
                        nextRows.map((item, index) => buildRowKey('usage', item, index))
                    );
                    const restoredSelection = new Set(
                        Array.from(storedSelection).filter((rowKey) => availableRowKeys.has(rowKey))
                    );

                    setSelectedRowKeys(restoredSelection);
                    setCompletedUsageTotalCostKrw(
                        restoredSelection.size > 0
                            ? calculateSelectedUsageTotalCostKrw(restoredSelection, nextRows)
                            : null
                    );
                    storeUsageSelection(restoredSelection);
                    usageWarningRevealTimerRef.current = window.setTimeout(() => {
                        setShowUsageWarningColors(true);
                        usageWarningRevealTimerRef.current = null;
                    }, 150);
                });
            });
            return;
        }

        fnc.sendAxios('/userRoutes/selectWalletTransactionHistory', {
            rowLimit: 2000
        }, (res) => {
            applyHistoryRows(res);
        });
    }

    const colSpan = tab === TAB.LOGIN
        ? 13
        : tab === TAB.MENU
            ? 12
            : tab === TAB.USAGE
                ? 23
                : 22;
    const noHeaderLabel = sortedRows.length;
    const usageCopyValue = copyCellValue !== null
        ? copyCellValue
        : completedUsageTotalCostKrw === null
            ? ''
            : completedUsageTotalCostKrw.toFixed(1);

    function getVisibleUsageCopyInput() {
        const mobileInput = usageCopyMobileInputRef.current;
        return mobileInput && mobileInput.offsetParent !== null
            ? mobileInput
            : usageCopyInputRef.current;
    }

    async function handleUsageValueCopy() {
        if (!usageCopyValue) return;

        let copied = false;

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(usageCopyValue);
                copied = true;
            }
        } catch (error) {
            copied = false;
        }

        const copyInput = getVisibleUsageCopyInput();
        if (!copied && copyInput) {
            copyInput.focus();
            copyInput.select();
            try {
                copied = document.execCommand('copy');
            } catch (error) {
                copied = false;
            }
        }

        setUsageCopyStatus(copied ? 'copied' : 'failed');
        if (usageCopyResetTimerRef.current) {
            window.clearTimeout(usageCopyResetTimerRef.current);
        }
        usageCopyResetTimerRef.current = window.setTimeout(() => {
            setUsageCopyStatus('idle');
        }, 1500);
    }

    function refreshButtonText() {
        if (loading) return '새로고침 중...';
        return '새로고침';
    }

    function handleSort(key) {
        setSortConfig((prev) => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc'
                };
            }

            return {
                key,
                direction: 'asc'
            };
        });
    }

    function renderHeader(label, key, className = '') {
        return (
            <th
                className={className}
                onClick={() => handleSort(key)}
                style={{ cursor: 'pointer' }}
                title="클릭해서 정렬"
            >
                {label}
            </th>
        );
    }

    function buildRowKey(prefix, item, index) {
        if (prefix === 'usage') {
            const usageId = item?.id;
            if (usageId !== null && usageId !== undefined && usageId !== '') {
                return `${prefix}-${usageId}`;
            }

            const fallbackParts = [
                item?.user_id,
                item?.stCode,
                item?.job_id,
                item?.service_name,
                item?.model_name,
                item?.created_at
            ];
            return `${prefix}-${fallbackParts.map((value) => value ?? '').join('-')}`;
        }

        const primaryId = item?.sno ?? item?.id;
        if (primaryId !== null && primaryId !== undefined && primaryId !== '') {
            return `${prefix}-${primaryId}-${index}`;
        }

        const userId = item?.user_id ?? '';
        const stCode = item?.stCode ?? '';
        const createdAt = item?.created_at ?? '';
        return `${prefix}-${userId}-${stCode}-${createdAt}-${index}`;
    }

    function renderCreatedAtCell(value, className = '') {
        const cellClassName = [isTodayDate(value) ? 'td-today-date' : '', className]
            .filter(Boolean)
            .join(' ');

        return (
            <td className={cellClassName}>
                {formatCell(value)}
            </td>
        );
    }

    function calculateSelectedUsageTotalCostKrw(selectedKeys, usageRows = sortedRows) {
        if (tab !== TAB.USAGE) return null;

        return usageRows.reduce((sum, item, index) => {
            const rowKey = buildRowKey('usage', item, index);
            if (!selectedKeys.has(rowKey)) return sum;

            const cost = Number(item.total_cost_krw);
            return Number.isFinite(cost) ? sum + cost : sum;
        }, 0);
    }

    function handleRowMouseDown(event, rowKey, prefix, index) {
        if (event.button !== 0) return;

        event.preventDefault();
        const preserveSelection = event.ctrlKey || event.metaKey;
        const mode = preserveSelection && selectedRowKeys.has(rowKey) ? 'remove' : 'add';
        const baseKeys = preserveSelection ? new Set(selectedRowKeys) : new Set();
        rowDragSelectionRef.current = {
            active: true,
            mode,
            prefix,
            startIndex: index,
            baseKeys,
            pendingCount: 0,
            pendingUsageTotalCostKrw: null
        };

        const nextKeys = new Set(baseKeys);
        if (mode === 'remove') nextKeys.delete(rowKey);
        else nextKeys.add(rowKey);
        rowDragSelectionRef.current.pendingCount = nextKeys.size;
        rowDragSelectionRef.current.pendingUsageTotalCostKrw = calculateSelectedUsageTotalCostKrw(nextKeys);
        setSelectedRowKeys(nextKeys);
        if (prefix === 'usage') storeUsageSelection(nextKeys);
    }

    function handleRowMouseEnter(event, prefix, index) {
        if (!rowDragSelectionRef.current.active) return;
        if ((event.buttons & 1) !== 1) {
            rowDragSelectionRef.current.active = false;
            return;
        }

        const dragState = rowDragSelectionRef.current;
        if (dragState.prefix !== prefix) return;

        const nextKeys = new Set(dragState.baseKeys);
        const firstIndex = Math.min(dragState.startIndex, index);
        const lastIndex = Math.max(dragState.startIndex, index);

        for (let rowIndex = firstIndex; rowIndex <= lastIndex; rowIndex += 1) {
            const rowItem = sortedRows[rowIndex];
            if (!rowItem) continue;
            const rangeRowKey = buildRowKey(prefix, rowItem, rowIndex);
            if (dragState.mode === 'remove') nextKeys.delete(rangeRowKey);
            else nextKeys.add(rangeRowKey);
        }

        rowDragSelectionRef.current.pendingCount = nextKeys.size;
        rowDragSelectionRef.current.pendingUsageTotalCostKrw = calculateSelectedUsageTotalCostKrw(nextKeys);
        setSelectedRowKeys(nextKeys);
        if (prefix === 'usage') storeUsageSelection(nextKeys);
    }

    function renderBodyRow(prefix, item, index, cells) {
        const rowKey = buildRowKey(prefix, item, index);

        return (
            <tr
                key={rowKey}
                className={selectedRowKeys.has(rowKey) ? 'selected' : ''}
                onMouseDown={(event) => handleRowMouseDown(event, rowKey, prefix, index)}
                onMouseEnter={(event) => handleRowMouseEnter(event, prefix, index)}
                onDragStart={(event) => event.preventDefault()}
            >
                {cells}
            </tr>
        );
    }

    function handleTableCellClick(event) {
        if (tab !== TAB.USAGE) return;

        const cell = event.target.closest('td');
        if (!cell || !event.currentTarget.contains(cell)) return;

        setCopyCellValue(cell.textContent.trim());
        setUsageCopyStatus('idle');
        window.requestAnimationFrame(() => {
            const copyInput = getVisibleUsageCopyInput();
            copyInput?.focus();
            copyInput?.select();
        });
    }

    return (
        <div className={`LoginHistory ${showUsageContents ? 'show-usage-contents' : ''} ${tab === TAB.ERRORS && !showUsageContents ? 'show-conversion-errors' : ''}`}>
            <div className="history-tab-buttons">
                <div className="history-tab-buttons-track">
                <button
                    type="button"
                    className="member-list-link"
                    onClick={() => navigate('/admin/users')}
                >
                    회원내역
                </button>
                <button
                    type="button"
                    className={!showUsageContents && tab === TAB.LOGIN ? 'active' : ''}
                    onClick={() => handleTabChange(TAB.LOGIN)}
                >
                    로그인
                </button>
                <button
                    type="button"
                    className={!showUsageContents && tab === TAB.MENU ? 'active' : ''}
                    onClick={() => handleTabChange(TAB.MENU)}
                >
                    메뉴클릭
                </button>
                <button
                    type="button"
                    className={!showUsageContents && tab === TAB.USAGE ? 'active' : ''}
                    onClick={() => handleTabChange(TAB.USAGE)}
                >
                    사용
                </button>
                <button
                    type="button"
                    className={!showUsageContents && tab === TAB.ERRORS ? 'active' : ''}
                    onClick={() => { setErrorJobId(''); handleTabChange(TAB.ERRORS); }}
                >
                    오류
                </button>
                <button
                    type="button"
                    className={!showUsageContents && tab === TAB.WALLET ? 'active' : ''}
                    onClick={() => handleTabChange(TAB.WALLET)}
                >
                    지갑거래
                </button>
                <button
                    type="button"
                    className={!showUsageContents && tab === TAB.QUEBANK ? 'active' : ''}
                    onClick={() => handleTabChange(TAB.QUEBANK)}
                >
                    QUEBANK
                </button>
                <button
                    type="button"
                    className="sms-send-link"
                    onClick={() => navigate('/admin/sms?tab=sms')}
                >
                    문자전송
                </button>
                <button
                    type="button"
                    className="sms-history-link"
                    onClick={() => navigate('/admin/sms?tab=history')}
                >
                    문자내역
                </button>
                {(tab === TAB.WALLET || tab === TAB.USAGE) && (
                    <WalletTransactionSummary
                        searchKeyword={activeSearchFilter}
                        billFilter={billFilter}
                        excludeMathautogen={excludeMathautogen}
                        refreshToken={summaryRefreshToken}
                        onTodayAmountChange={setTodayAmountTotal}
                        showTrigger={tab === TAB.WALLET}
                    />
                )}
                <button
                    type="button"
                    className={`usage-contents-trigger ${showUsageContents ? 'active' : ''}`}
                    onClick={() => setShowUsageContents(true)}
                >
                    사용내역
                </button>
                <span className="horizontal-scroll-end-spacer" aria-hidden="true" />
                </div>
            </div>

            {showUsageContents ? (
                <UsageContents
                    searchName={activeSearchFilter}
                    onSearchNameChange={handleFilterChange}
                />
            ) : tab === TAB.ERRORS ? (
                <ConversionErrorHistory key={errorJobId || 'all-errors'} initialJobId={errorJobId} />
            ) : tab === TAB.QUEBANK ? (
                <QuebankQuestionCount />
            ) : (
                <>
            <div className="history-toolbar">
                <div className="bill-filter-buttons">
                    <div className="bill-filter-buttons-track">
                    <button
                        type="button"
                        className={billFilter === BILL_FILTER.ALL ? 'active' : ''}
                        onClick={() => setBillFilter(BILL_FILTER.ALL)}
                    >
                        전체
                    </button>
                    <button
                        type="button"
                        className={billFilter === BILL_FILTER.MATHTAILER ? 'active' : ''}
                        onClick={() => setBillFilter(BILL_FILTER.MATHTAILER)}
                    >
                        mathtailer
                    </button>
                    <button
                        type="button"
                        className={billFilter === BILL_FILTER.KUESB_LMS ? 'active' : ''}
                        onClick={() => setBillFilter(BILL_FILTER.KUESB_LMS)}
                    >
                        kuesbLms
                    </button>
                    {(tab === TAB.WALLET || tab === TAB.USAGE) && (
                        <span className="wallet-today-amount wallet-today-amount--mobile">
                            <span className="wallet-today-amount-label">당일: </span>
                            {todayAmountTotal === null ? '조회 중...' : formatAmount(todayAmountTotal)}
                        </span>
                    )}
                    {tab === TAB.USAGE && (
                        <>
                            <input
                                ref={usageCopyMobileInputRef}
                                type="text"
                                className="usage-selected-total-cost usage-selected-total-cost--mobile"
                                value={usageCopyValue}
                                aria-label="클릭한 셀 값 복사용"
                                title="클릭한 셀 값 복사용"
                                onFocus={(event) => event.target.select()}
                                readOnly
                            />
                            <button
                                type="button"
                                className={`btn-copy-usage-value btn-copy-usage-value--mobile ${usageCopyStatus}`}
                                onClick={handleUsageValueCopy}
                                disabled={!usageCopyValue}
                            >
                                {usageCopyStatus === 'copied'
                                    ? '복사됨'
                                    : usageCopyStatus === 'failed'
                                        ? '복사 실패'
                                        : '복사'}
                            </button>
                        </>
                    )}
                    <span className="horizontal-scroll-end-spacer" aria-hidden="true" />
                    </div>
                </div>

                <div className="history-filter-box">
                    <div className="filter-grid">
                        <label>
                            <input
                                type="text"
                                value={activeSearchFilter}
                                onChange={(event) => handleFilterChange(event.target.value)}
                            />
                        </label>
                        <label className="chk-exclude-myid">
                            <input
                                type="radio"
                                name="mathautogen-filter"
                                checked={excludeMathautogen}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    setExcludeMathautogen(checked);
                                    if (checked) setShowMathautogenOnly(false);
                                }}
                            />
                            <span>MY제거</span>
                        </label>
                        <label className="chk-exclude-myid">
                            <input
                                type="radio"
                                name="mathautogen-filter"
                                checked={showMathautogenOnly}
                                onChange={(event) => {
                                    const checked = event.target.checked;
                                    setShowMathautogenOnly(checked);
                                    if (checked) setExcludeMathautogen(false);
                                }}
                            />
                            <span>MY</span>
                        </label>
                    </div>
                    <div className="filter-actions">
                        <button
                            type="button"
                            onClick={handleResetFilters}
                            disabled={!hasActiveFilters}
                        >
                            초기화
                        </button>
                        <button
                            type="button"
                            className="btn-refresh"
                            onClick={handleRefresh}
                            disabled={loading}
                        >
                            {refreshButtonText()}
                        </button>
                        {(tab === TAB.WALLET || tab === TAB.USAGE) && (
                            <span className="wallet-today-amount wallet-today-amount--toolbar">
                                <span className="wallet-today-amount-label">당일: </span>
                                {todayAmountTotal === null ? '조회 중...' : formatAmount(todayAmountTotal)}
                            </span>
                        )}
                        {tab === TAB.USAGE && (
                            <>
                                <input
                                    ref={usageCopyInputRef}
                                    type="text"
                                    className="usage-selected-total-cost usage-selected-total-cost--toolbar"
                                    value={usageCopyValue}
                                    aria-label="클릭한 셀 값 복사용"
                                    title="클릭한 셀 값 복사용"
                                    onFocus={(event) => event.target.select()}
                                    readOnly
                                />
                                <button
                                    type="button"
                                    className={`btn-copy-usage-value btn-copy-usage-value--toolbar ${usageCopyStatus}`}
                                    onClick={handleUsageValueCopy}
                                    disabled={!usageCopyValue}
                                >
                                    {usageCopyStatus === 'copied'
                                        ? '복사됨'
                                        : usageCopyStatus === 'failed'
                                            ? '복사 실패'
                                            : '복사'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <div className="history-table-wrap">
                <table className="history-table">
                    <thead>
                        {tab === TAB.LOGIN ? (
                            <tr>
                                {renderHeader(noHeaderLabel, 'sno')}
                                {renderHeader('created_at', 'created_at', 'col-login-center')}
                                {renderHeader('이름', 'user_name', 'col-login-center')}
                                {renderHeader('appVer', 'app_version', 'col-login-version')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('os_version', 'os_version', 'col-login-os-version')}
                                {renderHeader('app_name', 'app_name')}
                                {renderHeader('machine_name', 'machine_name')}
                                {renderHeader('os_user', 'os_user')}
                                {renderHeader('bill_id', 'bill_id')}
                                {renderHeader('login_type', 'login_type')}
                                {renderHeader('success_yn', 'success_yn')}
                                {renderHeader('fail_reason', 'fail_reason')}
                            </tr>
                        ) : tab === TAB.MENU ? (
                            <tr>
                                {renderHeader(noHeaderLabel, 'sno')}
                                {renderHeader('created_at', 'created_at', 'col-menu-center')}
                                {renderHeader('이름', 'user_name', 'col-menu-center')}
                                {renderHeader('menu_name', 'menu_name', 'col-menu-center')}
                                {renderHeader('balance_after', 'balance_after', 'col-menu-balance')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('menu_source', 'menu_source', 'col-menu-center')}
                                {renderHeader('app_name', 'app_name', 'col-menu-center')}
                                {renderHeader('machine_name', 'machine_name')}
                                {renderHeader('os_user', 'os_user')}
                                {renderHeader('os_version', 'os_version')}
                                {renderHeader('bill_id', 'bill_id')}
                            </tr>
                        ) : tab === TAB.USAGE ? (
                            <tr>
                                {renderHeader(noHeaderLabel, 'sno')}
                                {renderHeader('created_at', 'created_at', 'col-usage-center')}
                                {renderHeader('이름', 'user_name', 'col-usage-center')}
                                {renderHeader('appVer', 'app_version', 'col-usage-version')}
                                {renderHeader('sno', 'id', 'col-usage-center')}
                                {renderHeader('service_name', 'service_name', 'col-usage-service-name')}
                                {renderHeader('금액', 'wallet_amount', 'col-usage-wallet-amount')}
                                {renderHeader('원가', 'original_cost', 'col-usage-wallet-amount')}
                                {renderHeader('이익', 'profit', 'col-usage-wallet-amount')}
                                {renderHeader('마진율', 'margin_rate', 'col-usage-wallet-amount')}
                                <th
                                    className="col-usage-count"
                                    title="PDF2HWP2 개수 / PDF 이미지 처리 개수"
                                >
                                    cnt
                                </th>
                                <th className="col-usage-duration" title="job_id 그룹의 created_at 최대-최소 시간">
                                    시간
                                </th>
                                {renderHeader('model_name', 'model_name')}
                                {renderHeader('page', 'page_number', 'col-usage-center')}
                                {renderHeader('total_cost_krw', 'total_cost_krw', 'col-usage-center')}
                                {renderHeader('input_cost_usd', 'input_cost_usd', 'col-usage-center')}
                                {renderHeader('output_cost_usd', 'output_cost_usd', 'col-usage-center')}
                                {renderHeader('total_cost_usd', 'total_cost_usd', 'col-usage-center')}
                                {renderHeader('fileName', 'wallet_file_name', 'col-usage-wallet-file-name')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('job_id', 'job_id', 'col-job-id')}
                                {renderHeader('success_yn', 'success_yn', 'col-usage-center')}
                                {renderHeader('bill_id', 'bill_id', 'col-usage-center')}
                            </tr>
                        ) : (
                            <tr>
                                {renderHeader(noHeaderLabel, 'id')}
                                {renderHeader('created_at', 'created_at', 'col-wallet-center')}
                                {renderHeader('이름', 'user_name', 'col-wallet-center')}
                                {renderHeader('appVer', 'app_version', 'col-wallet-version')}
                                {renderHeader('ref_type', 'ref_type', 'col-wallet-center')}
                                {renderHeader('금액', 'amount', 'col-wallet-center col-wallet-finance col-wallet-amount')}
                                {renderHeader('원가', 'original_cost', 'col-wallet-finance col-wallet-metric-center')}
                                {renderHeader('이익', 'profit', 'col-wallet-finance col-wallet-metric-center col-wallet-profit')}
                                {renderHeader('마진율', 'margin_rate', 'col-wallet-finance col-wallet-metric-center')}
                                {renderHeader('cnt', 'pageCnt', 'col-wallet-center col-wallet-count')}
                                <th className="col-wallet-duration" title="job_id 그룹의 created_at 최대-최소 시간">
                                    시간
                                </th>
                                {renderHeader('balance_after', 'balance_after', 'col-wallet-center')}
                                {renderHeader('남은 비율', 'remaining_ratio', 'col-wallet-center')}
                                {renderHeader('마지막 충전금액', 'last_charge_amount', 'col-wallet-center')}
                                {renderHeader('총 충전금액', 'total_charged', 'col-wallet-center')}
                                {renderHeader('fileName', 'fileName', 'col-wallet-file-name')}
                                {renderHeader('trans_page', 'trans_page', 'col-wallet-trans-page')}
                                {renderHeader('total_page', 'total_page')}
                                {renderHeader('bill_id', 'bill_id')}
                                {renderHeader('user_id', 'user_id')}
                                {renderHeader('job_id', 'job_id', 'col-job-id')}
                                {renderHeader('ref_id', 'ref_id')}
                            </tr>
                        )}
                    </thead>
                    <tbody onClick={handleTableCellClick}>
                        {loading ? (
                            <tr>
                                <td colSpan={colSpan} className="empty">조회 중...</td>
                            </tr>
                        ) : sortedRows.length === 0 ? (
                            <tr>
                                <td colSpan={colSpan} className="empty">검색 결과가 없습니다.</td>
                            </tr>
                        ) : tab === TAB.LOGIN ? (
                            sortedRows.map((item, index) => (
                                renderBodyRow('login', item, index, <>
                                    <td>{index + 1}</td>
                                    {renderCreatedAtCell(item.created_at, 'col-login-center')}
                                    <td className='td-user-name col-login-center'>{formatCell(item.user_name)}</td>
                                    <td className='col-login-version'>{formatCell(item.app_version)}</td>
                                    <td>{formatCell(item.user_id)}</td>
                                    <td className="col-login-os-version" title={formatCell(item.os_version)}>
                                        {formatCell(item.os_version)}
                                    </td>
                                    <td>{formatCell(item.app_name)}</td>
                                    <td>{formatCell(item.machine_name)}</td>
                                    <td>{formatCell(item.os_user)}</td>
                                    <td>{formatCell(item.bill_id)}</td>
                                    <td>{formatCell(item.login_type)}</td>
                                    <td>{formatCell(item.success_yn)}</td>
                                    <td>{formatCell(item.fail_reason)}</td>
                                </>)
                            ))
                        ) : tab === TAB.MENU ? (
                            sortedRows.map((item, index) => (
                                renderBodyRow('menu', item, index, <>
                                    <td>{index + 1}</td>
                                    {renderCreatedAtCell(item.created_at, 'col-menu-center')}
                                    <td className='td-user-name col-menu-center'>{formatCell(item.user_name)}</td>
                                    <td className={`td-menu-name col-menu-center ${
                                        ['충전하기', '충전하기 테스트'].includes(formatCell(item.menu_name).trim())
                                            ? 'td-menu-name-charge-test'
                                            : ''
                                    }`}>
                                        {formatCell(item.menu_name)}
                                    </td>
                                    <td className="col-menu-balance">{formatNumberCell(item.balance_after)}</td>
                                    <td>{formatCell(item.user_id)}</td>
                                    <td className='col-menu-center'>{formatCell(item.menu_source)}</td>
                                    <td className='col-menu-center'>{formatCell(item.app_name)}</td>
                                    <td>{formatCell(item.machine_name)}</td>
                                    <td>{formatCell(item.os_user)}</td>
                                    <td>{formatCell(item.os_version)}</td>
                                    <td>{formatCell(item.bill_id)}</td>
                                </>)
                            ))
                        ) : tab === TAB.USAGE ? (
                            sortedRows.map((item, index) => {
                                const jobId = formatCell(item.job_id).trim();
                                const previousJobId = index > 0
                                    ? formatCell(sortedRows[index - 1]?.job_id).trim()
                                    : '';
                                const isFirstJobRow = !jobId || jobId !== previousJobId;
                                let jobRowSpan = 1;

                                if (jobId && isFirstJobRow) {
                                    while (
                                        index + jobRowSpan < sortedRows.length
                                        && formatCell(sortedRows[index + jobRowSpan]?.job_id).trim() === jobId
                                    ) {
                                        jobRowSpan += 1;
                                    }
                                }
                                const mergedJobRows = isFirstJobRow
                                    ? sortedRows.slice(index, index + jobRowSpan)
                                    : [];
                                const pdf2Hwp2Count = mergedJobRows.filter(
                                    (row) => String(row?.service_name || '').trim().toUpperCase() === 'PDF2HWP2'
                                ).length;
                                const pdf2ImageProcessCount = mergedJobRows.filter(
                                    (row) => PDF2_IMAGE_PROCESS_SERVICE_NAMES.has(
                                        String(row?.service_name || '').trim().toUpperCase()
                                    )
                                ).length;
                                const createdAtDuration = formatDurationSeconds(item.job_duration_seconds);
                                const normalizedUsageServiceName = normalizeFilterValue(item.service_name).replace(/\s+/g, '');
                                const isVariationAiService = normalizedUsageServiceName === '변형ai';
                                const isEmptyAmountWarningService = !['풀이ai', '이미지ai', '변형ai'].includes(normalizedUsageServiceName);
                                const emptyAmountClass = (value) => {
                                    if (formatNumberCell(value) !== '') return '';
                                    if (isVariationAiService) return 'td-usage-empty-amount-yellow';
                                    return isEmptyAmountWarningService && showUsageWarningColors
                                        ? 'td-usage-empty-amount-warning'
                                        : '';
                                };
                                const hasEmptyUsageAmount = [
                                    item.wallet_amount,
                                    item.original_cost,
                                    item.profit,
                                    item.margin_rate
                                ].some((value) => formatNumberCell(value) === '');
                                const emptyAmountNameClass = hasEmptyUsageAmount
                                    ? isVariationAiService
                                        ? 'td-usage-empty-amount-yellow'
                                        : isEmptyAmountWarningService
                                            ? showUsageWarningColors
                                                ? 'td-usage-empty-amount-warning'
                                                : ''
                                            : 'td-usage-empty-amount-green'
                                    : '';

                                return renderBodyRow('usage', item, index, <>
                                    <td>{index + 1}</td>
                                    {renderCreatedAtCell(item.created_at, 'col-usage-center')}
                                    <td className={`td-user-name col-usage-center usage-group-name-${usageGroupColorIndexes[index]} ${emptyAmountNameClass}`}>{formatCell(item.user_name)}</td>
                                    <td className='col-usage-version'>{formatCell(item.app_version)}</td>
                                    <td className='col-usage-center'>{formatCell(item.id)}</td>
                                    <td
                                        className={`td-service-name col-usage-service-name ${
                                            String(item.service_name || '').toUpperCase() === 'PDF2HWP2'
                                                ? 'td-service-pdf2hwp2'
                                                : String(item.service_name || '').toUpperCase() === 'PDF2_IMAGE_ALL_IMAGES_SIMPLE'
                                                    ? ''
                                                : String(item.service_name || '').toUpperCase() === 'PDF2_IMAGE_COUNT_FAST'
                                                    ? 'td-service-pdf2-image-fast'
                                                : String(item.service_name || '').toUpperCase().startsWith('PDF2_IMAGE_')
                                                    ? 'td-service-pdf2-image'
                                                    : ''
                                        }`}
                                    >
                                        {formatCell(item.service_name)}
                                    </td>
                                    {isFirstJobRow && (
                                        <td className={`col-usage-wallet-amount td-usage-job-merged ${emptyAmountClass(item.wallet_amount)}`} rowSpan={jobRowSpan}>
                                            {formatNumberCell(item.wallet_amount)}
                                        </td>
                                    )}
                                    {isFirstJobRow && (
                                        <td className={`col-usage-wallet-amount td-usage-job-merged ${emptyAmountClass(item.original_cost)}`} rowSpan={jobRowSpan}>
                                            {formatNumberCell1(item.original_cost)}
                                        </td>
                                    )}
                                    {isFirstJobRow && (
                                        <td className={`col-usage-wallet-amount td-usage-job-merged ${getFinancialSignClass(item.profit)} ${emptyAmountClass(item.profit)}`} rowSpan={jobRowSpan}>
                                            {formatNumberCell1(item.profit)}
                                        </td>
                                    )}
                                    {isFirstJobRow && (
                                        <td
                                            className={`col-usage-wallet-amount td-usage-job-merged td-margin-rate ${getFinancialSignClass(item.margin_rate)} ${emptyAmountClass(item.margin_rate)}`}
                                            rowSpan={jobRowSpan}
                                        >
                                            {formatNumberCell1(item.margin_rate)}
                                        </td>
                                    )}
                                    {isFirstJobRow && (
                                        <td
                                            className="col-usage-count td-usage-job-merged"
                                            rowSpan={jobRowSpan}
                                            title={`PDF2HWP2 ${pdf2Hwp2Count}개 / PDF 이미지 처리 ${pdf2ImageProcessCount}개`}
                                        >
                                            {pdf2Hwp2Count}/{pdf2ImageProcessCount}
                                        </td>
                                    )}
                                    {isFirstJobRow && (
                                        <td
                                            className="col-usage-duration td-usage-job-merged"
                                            rowSpan={jobRowSpan}
                                            title="job_id 그룹의 created_at 최대-최소 시간"
                                        >
                                            {createdAtDuration}
                                        </td>
                                    )}
                                    <td className={
                                        String(item.model_name || '').toLowerCase() === 'gemini-3-flash-preview'
                                            ? 'td-model-gemini-flash'
                                            : String(item.model_name || '').toLowerCase() === 'gemini-3.1-pro-preview'
                                                ? 'td-model-gemini-pro'
                                                : ''
                                    }>
                                        {formatCell(item.model_name)}
                                    </td>
                                    <td className='col-usage-center'>{formatCell(item.page_number)}</td>
                                    <td className='col-usage-currency'>{formatCost4(item.total_cost_krw)}</td>
                                    <td className='col-usage-currency'>{formatCost4(item.input_cost_usd)}</td>
                                    <td className='col-usage-currency'>{formatCost4(item.output_cost_usd)}</td>
                                    <td className='col-usage-currency'>{formatCost4(item.total_cost_usd)}</td>
                                    {isFirstJobRow && (
                                        <td
                                            className='col-usage-wallet-file-name td-usage-job-merged'
                                            rowSpan={jobRowSpan}
                                            title={formatCell(item.wallet_file_name)}
                                        >
                                            {formatCell(item.wallet_file_name)}
                                        </td>
                                    )}
                                    <td>{formatCell(item.user_id)}</td>
                                    {isFirstJobRow && (
                                        <td className='col-job-id td-usage-job-merged td-usage-job-id-merged' rowSpan={jobRowSpan}>
                                            {jobId}
                                            {jobId && <button
                                                type="button"
                                                className="usage-job-errors-link"
                                                onMouseDown={event => event.stopPropagation()}
                                                onClick={event => {
                                                    event.stopPropagation();
                                                    setErrorJobId(jobId);
                                                    handleTabChange(TAB.ERRORS);
                                                }}
                                            >오류 보기</button>}
                                        </td>
                                    )}
                                    <td className='col-usage-center'>{formatCell(item.success_yn)}</td>
                                    <td className='col-usage-center'>{formatCell(item.bill_id)}</td>
                                </>);
                            })
                        ) : (
                            sortedRows.map((item, index) => {
                                const remainingRatio = formatRemainingRatio(item.balance_after, item.last_charge_amount);
                                const formattedRefType = formatWalletRefType(item.ref_type, item.ref_id);
                                const createdAtDuration = formatDurationSeconds(item.job_duration_seconds);

                                return renderBodyRow('wallet', item, index, <>
                                    <td>{index + 1}</td>
                                    {renderCreatedAtCell(item.created_at, 'col-wallet-center')}
                                    <td className='td-user-name col-wallet-center'>{formatCell(item.user_name)}</td>
                                    <td className='col-wallet-version'>{formatCell(item.app_version)}</td>
                                    <td className={`td-ref-type col-wallet-center ${
                                        formattedRefType === 'CHARGE'
                                            ? 'td-ref-type-charge'
                                            : formattedRefType === 'PDF_PRO1'
                                                ? 'td-ref-type-pdf-pro1'
                                                : formattedRefType === 'PDF_PRO2'
                                                    ? 'td-ref-type-pdf-pro2'
                                                    : ''
                                    }`}>
                                        {formattedRefType}
                                    </td>
                                    <td className={`col-wallet-center col-wallet-finance col-wallet-amount ${Number(item.amount) > 0 ? 'td-wallet-amount-positive' : ''}`}>
                                        {formatNumberCell(item.amount)}
                                    </td>
                                    <td className='col-wallet-finance col-wallet-metric-center'>{formatRoundedNumberCell(item.original_cost)}</td>
                                    <td className={`col-wallet-finance col-wallet-metric-center col-wallet-profit ${getFinancialSignClass(item.profit)}`}>{formatRoundedNumberCell(item.profit)}</td>
                                    <td className={`col-wallet-finance col-wallet-metric-center td-margin-rate ${getFinancialSignClass(item.margin_rate)}`}>
                                        {formatNumberCell1(item.margin_rate)}
                                    </td>
                                    <td className='col-wallet-center col-wallet-count'>{formatCell(item.pageCnt)}</td>
                                    <td
                                        className='col-wallet-center col-wallet-duration'
                                        title="job_id 그룹의 created_at 최대-최소 시간"
                                    >
                                        {createdAtDuration}
                                    </td>
                                    <td className='td-wallet-balance-after col-wallet-center'>{formatNumberCell(item.balance_after)}</td>
                                    <td className="td-wallet-remaining-ratio col-wallet-center">{remainingRatio}</td>
                                    <td className='col-wallet-center'>{formatNumberCell(item.last_charge_amount)}</td>
                                    <td className="td-total-charged col-wallet-center">{formatNumberCell(item.total_charged)}</td>
                                    <td
                                        className="col-wallet-file-name"
                                        title={formatCell(item.fileName)}
                                    >
                                        {formatCell(item.fileName)}
                                    </td>
                                    <td className="col-wallet-trans-page">{formatCell(item.trans_page)}</td>
                                    <td>{formatCell(item.total_page)}</td>
                                    <td>{formatCell(item.bill_id)}</td>
                                    <td>{formatCell(item.user_id)}</td>
                                    <td className='col-job-id'>{formatCell(item.job_id)}</td>
                                    <td>{formatCell(item.ref_id)}</td>
                                </>);
                            })
                        )}
                    </tbody>
                </table>
            </div>
                </>
            )}
        </div>
    );
};

export default LoginHistory;
